import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { toastActions } from "./toast";
import { currentUserActions } from "./currentUser";
import { getRandId, readURIFromFile } from "../libs";
import { PanoramaService, PanoramaAfterImage, PanoramaBeforeImage } from "../api/panorama";
import { PanoramaMaxFileSize, PanoramaMaxRegenCount, PanoramaMaxExecTime, PanoramaAllowedFormats, PanoramaMaxUpload } from "../constants";
import { NavigateFunction } from "react-router-dom";

// State型定義
export interface PanoramaBeforeImageState {
  id?: number;
  panoramaDesignId?: number;
  stubId: string;
  file: File;
  uri: string;
  fileName: string;
  regenerationCount: number;
  afterImages: PanoramaAfterImageState[];
  selectedAfterImageId: number | null;
  isProcessing?: boolean;
  errorMessage?: string | null;
}

export interface PanoramaAfterImageState {
  id: number;
  fileName: string;
  fileUrl: string;
  downloadUrl: string;
}

export interface PanoramaState {
  isLoading: boolean;
  stubId: string;
  beforeImages: PanoramaBeforeImageState[];
  selectedBeforeImageStubId: string | null;
  afterImages: PanoramaAfterImageState[];
  selectedAfterImageId: number | null;
  regenerationCount: number;
  runTime: number;
  maxExecTime: number;

  // UI状態
  isConfirmPopupShow: boolean;
  isRegenPopupShow: boolean;
  isPhotoTooBigPopupShow: boolean;
  isAspectRatioErrorShow: boolean;
  isClearAllPopupShow: boolean;
  errorMessage: string | null;

  // 生成進捗管理
  generatingImages: Record<string, { current: number; total: number }>;
}

// アプリケーションのRootState型
interface RootState {
  panorama: PanoramaState;
  [key: string]: unknown;
}

const initialState: PanoramaState = {
  isLoading: false,
  stubId: getRandId().toString(),
  beforeImages: [],
  selectedBeforeImageStubId: null,
  afterImages: [],
  selectedAfterImageId: null,
  regenerationCount: 0,
  runTime: 0,
  maxExecTime: PanoramaMaxExecTime,

  // UI状態
  isConfirmPopupShow: false,
  isRegenPopupShow: false,
  isPhotoTooBigPopupShow: false,
  isAspectRatioErrorShow: false,
  isClearAllPopupShow: false,
  errorMessage: null,

  // 生成進捗管理
  generatingImages: {},
};

// アスペクト比チェック（2:1 ± 5%）
const validatePanoramaAspectRatio = (width: number, height: number, tolerance: number = 0.05): boolean => {
  const aspectRatio = width / height;
  const expectedRatio = 2.0;
  return Math.abs(aspectRatio - expectedRatio) / expectedRatio <= tolerance;
};

// 画像ファイルのバリデーション
const validatePanoramaFile = async (file: File): Promise<{ valid: boolean; error?: string }> => {
  // ファイルサイズチェック
  if (file.size > PanoramaMaxFileSize) {
    return { valid: false, error: 'file_too_big' };
  }

  // フォーマットチェック
  if (!PanoramaAllowedFormats.includes(file.type as any)) {
    return { valid: false, error: 'invalid_format' };
  }

  // アスペクト比チェック（一時的に無効化）
  // return new Promise((resolve) => {
  //   const img = new Image();
  //   img.onload = () => {
  //     const valid = validatePanoramaAspectRatio(img.width, img.height);
  //     resolve({ valid, error: valid ? undefined : 'invalid_aspect_ratio' });
  //   };
  //   img.onerror = () => {
  //     resolve({ valid: false, error: 'invalid_image' });
  //   };
  //   img.src = URL.createObjectURL(file);
  // });
  return { valid: true };
};

// 画像アップロード処理（複数ファイル対応）
export const uploadPanoramaImages = createAsyncThunk<void, FileList, { state: RootState }>(
  "panorama/uploadPanoramaImages",
  async (files, thunkAPI) => {
    const { beforeImages } = thunkAPI.getState().panorama;
    const newImages = [...beforeImages];

    for (const file of Array.from(files)) {
      // 最大枚数チェック
      if (newImages.length >= PanoramaMaxUpload) {
        thunkAPI.dispatch(toastActions.toastError(`アップロードは最大${PanoramaMaxUpload}枚までです`));
        break;
      }

      // バリデーション
      const validation = await validatePanoramaFile(file);
      if (!validation.valid) {
        if (validation.error === 'file_too_big') {
          thunkAPI.dispatch(panoramaActions.setIsPhotoTooBigPopupShow(true));
        } else if (validation.error === 'invalid_aspect_ratio') {
          thunkAPI.dispatch(panoramaActions.setIsAspectRatioErrorShow(true));
        } else {
          thunkAPI.dispatch(toastActions.toastError("画像ファイルの形式が不正です"));
        }
        continue;
      }

      // 画像をbase64に変換
      const uri = await readURIFromFile(file);
      const stubId = getRandId().toString();

      const beforeImage: PanoramaBeforeImageState = {
        stubId,
        file,
        uri,
        fileName: file.name,
        regenerationCount: 0,
        afterImages: [],
        selectedAfterImageId: null,
      };

      newImages.push(beforeImage);
    }

    thunkAPI.dispatch(panoramaActions.setBeforeImages(newImages));

    // 最後に追加された画像を選択
    if (newImages.length > beforeImages.length) {
      const lastAddedImage = newImages[newImages.length - 1];
      thunkAPI.dispatch(panoramaActions.setSelectedBeforeImageStubId(lastAddedImage.stubId));
    }
  }
);

// 選択中のbeforeImageを取得するヘルパー
const getSelectedBeforeImage = (state: RootState): PanoramaBeforeImageState | null => {
  const { beforeImages, selectedBeforeImageStubId } = state.panorama;
  if (!selectedBeforeImageStubId) return null;
  return beforeImages.find(img => img.stubId === selectedBeforeImageStubId) || null;
};

// APIレスポンスを処理してstateを更新する共通関数
const processApiResponse = (
  stubId: string,
  response: any,
  thunkAPI: any,
  isFirstCall: boolean = false
): PanoramaAfterImageState[] => {
  // afterImagesの設定（バックエンドからのレスポンスをそのまま使用）
  const afterImages: PanoramaAfterImageState[] = response.afterimages.map((img: PanoramaAfterImage) => ({
    id: img.id,
    fileName: img.file_name,
    fileUrl: img.file,
    downloadUrl: img.downloadurl,
  }));

  // beforeImageの更新
  if (isFirstCall) {
    thunkAPI.dispatch(panoramaActions.updateBeforeImageFromResponse({
      stubId: stubId,
      id: response.id,
      panoramaDesignId: response.panorama_design_id,
      regenerationCount: response.regeneration_count,
      afterImages,
      selectedAfterImageId: afterImages.length > 0 ? afterImages[0].id : null,
    }));
  } else {
    thunkAPI.dispatch(panoramaActions.updateBeforeImageFromResponse({
      stubId: stubId,
      afterImages,
    }));
  }

  // グローバルのafterImagesも更新（表示用）
  thunkAPI.dispatch(panoramaActions.setAfterImages(afterImages));
  if (afterImages.length > 0) {
    // 新しく追加された画像を選択
    const latestImage = afterImages[afterImages.length - 1];
    thunkAPI.dispatch(panoramaActions.setSelectedAfterImageId(latestImage.id));
  }

  return afterImages;
};

// 単一画像の処理（内部用）- 1枚ずつ3回API呼び出し
const processOneImage = async (
  beforeImage: PanoramaBeforeImageState,
  thunkAPI: any
): Promise<boolean> => {
  const stubId = beforeImage.stubId;

  // 進捗を初期化（合計3枚）
  thunkAPI.dispatch(panoramaActions.setGenerationProgress({
    stubId,
    current: 0,
    total: 3,
  }));

  try {
    // 1枚目: 初回生成API呼び出し
    const response = await PanoramaService.processPanorama(stubId, beforeImage.file);

    if (response.success === false) {
      const errorMsg = response.message || "生成に失敗しました";
      thunkAPI.dispatch(panoramaActions.updateBeforeImageFromResponse({
        stubId,
        errorMessage: errorMsg,
      }));
      thunkAPI.dispatch(toastActions.toastError(errorMsg));
      // エラー時は進捗をクリア
      thunkAPI.dispatch(panoramaActions.clearGenerationProgress(stubId));
      return false;
    }

    // 1枚目の結果を即座に表示
    let generatedCount = 1;
    processApiResponse(stubId, response, thunkAPI, true);

    // 進捗更新: 1/3
    thunkAPI.dispatch(panoramaActions.setGenerationProgress({
      stubId,
      current: 1,
      total: 3,
    }));

    // 2枚目・3枚目: 再生成API呼び出し（計3枚にするため2回呼び出し）
    // ※初回生成なのでis_initial=trueを渡す（DB上で初回生成として記録）
    let regenFailCount = 0;
    for (let i = 0; i < 2; i++) {
      try {
        const regenResponse = await PanoramaService.regenPanorama(response.id, true);
        if (regenResponse.success === false) {
          console.error(`Regen ${i + 1} failed:`, regenResponse.message);
          regenFailCount++;
          // 失敗してもループを続行（1枚でも多く生成する）
          continue;
        }
        if (regenResponse.afterimages) {
          generatedCount++;
          // 各画像の結果を即座に表示
          processApiResponse(stubId, regenResponse, thunkAPI, false);

          // 進捗更新
          thunkAPI.dispatch(panoramaActions.setGenerationProgress({
            stubId,
            current: generatedCount,
            total: 3,
          }));
        }
      } catch (regenError) {
        console.error(`Regen ${i + 1} error:`, regenError);
        regenFailCount++;
        // エラーでもループを続行
        continue;
      }
    }

    // 全てのregenが失敗した場合はエラーメッセージを設定
    if (regenFailCount === 2) {
      thunkAPI.dispatch(panoramaActions.updateBeforeImageFromResponse({
        stubId,
        errorMessage: "追加画像の生成に失敗しました",
      }));
    }

    // 3枚未満の場合は警告
    if (generatedCount < 3) {
      console.warn(`Only ${generatedCount}/3 images generated for stubId: ${stubId}`);
    }

    // 完了時は進捗を実際の生成数に設定（UIで「生成完了」表示用）
    thunkAPI.dispatch(panoramaActions.setGenerationProgress({
      stubId,
      current: generatedCount,
      total: generatedCount,
    }));

    return generatedCount > 0;
  } catch (error) {
    console.error('processOneImage error:', error);
    thunkAPI.dispatch(panoramaActions.updateBeforeImageFromResponse({
      stubId,
      errorMessage: "処理中にエラーが発生しました",
    }));
    thunkAPI.dispatch(toastActions.toastError("処理中にエラーが発生しました"));
    // エラー時は進捗をクリア
    thunkAPI.dispatch(panoramaActions.clearGenerationProgress(stubId));
    return false;
  }
};

// 初回生成処理（全ての未処理画像を処理）
const processPanorama = createAsyncThunk<void, NavigateFunction, { state: RootState }>(
  "panorama/processPanorama",
  async (navigate, thunkAPI) => {
    const state = thunkAPI.getState();
    const { beforeImages } = state.panorama;

    // 未処理の画像を取得
    const unprocessedImages = beforeImages.filter(img => !img.id);

    if (unprocessedImages.length === 0) {
      thunkAPI.dispatch(toastActions.toastError("処理する画像がありません"));
      return;
    }

    thunkAPI.dispatch(panoramaActions.setIsLoading(true));
    thunkAPI.dispatch(panoramaActions.setErrorMessage(null));

    // タイマー開始
    let runTime = 0;
    thunkAPI.dispatch(panoramaActions.setRunTime(runTime));
    const timer = setInterval(() => {
      thunkAPI.dispatch(panoramaActions.setRunTime(runTime += 1));
    }, 1000);

    let successCount = 0;
    let failCount = 0;

    try {
      // 全ての未処理画像を並列で処理（各画像内の3枚生成は順次）
      const results = await Promise.all(
        unprocessedImages.map(beforeImage => processOneImage(beforeImage, thunkAPI))
      );

      // 結果を集計
      results.forEach(success => {
        if (success) {
          successCount++;
        } else {
          failCount++;
        }
      });

      // ユーザー情報を更新（クレジット消費を反映）
      thunkAPI.dispatch(currentUserActions.getCurrentUser(navigate));

      // 結果通知
      if (failCount > 0) {
        thunkAPI.dispatch(toastActions.toastError(`${failCount}枚の画像の処理に失敗しました`));
      }
      if (successCount > 0) {
        thunkAPI.dispatch(toastActions.toastSuccess(`${successCount}枚の画像を処理しました`));
      }

    } catch (error) {
      console.error('processPanorama error:', error);
      thunkAPI.dispatch(panoramaActions.setErrorMessage("処理中にエラーが発生しました"));
      thunkAPI.dispatch(toastActions.toastError("処理中にエラーが発生しました"));
    } finally {
      clearInterval(timer);
      thunkAPI.dispatch(panoramaActions.setIsLoading(false));
    }
  }
);

// 再生成処理（3枚追加、1枚ずつAPI呼び出し）
const regenPanorama = createAsyncThunk<void, void, { state: RootState }>(
  "panorama/regenPanorama",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedBeforeImage = getSelectedBeforeImage(state);

    if (!selectedBeforeImage || !selectedBeforeImage.id) {
      thunkAPI.dispatch(toastActions.toastError("画像が処理されていません"));
      return;
    }

    const currentRegenCount = selectedBeforeImage.regenerationCount || 0;
    if (currentRegenCount >= PanoramaMaxRegenCount) {
      thunkAPI.dispatch(toastActions.toastError(`再生成は${PanoramaMaxRegenCount}回までです`));
      return;
    }

    thunkAPI.dispatch(panoramaActions.setIsLoading(true));

    // タイマー開始
    let runTime = 0;
    thunkAPI.dispatch(panoramaActions.setRunTime(runTime));
    const timer = setInterval(() => {
      thunkAPI.dispatch(panoramaActions.setRunTime(runTime += 1));
    }, 1000);

    try {
      // 再生成API呼び出し（3回呼び出して1枚ずつ追加・表示）
      for (let i = 0; i < 3; i++) {
        const regenResponse = await PanoramaService.regenPanorama(selectedBeforeImage.id);
        if (regenResponse.success === false) {
          if (i === 0) {
            thunkAPI.dispatch(toastActions.toastError(regenResponse.message || "再生成に失敗しました"));
            return;
          }
          break;
        }

        // 各画像の結果を即座に表示
        processApiResponse(selectedBeforeImage.stubId, regenResponse, thunkAPI, false);
      }

      // 再生成カウント増加API呼び出し
      const incrementResponse = await PanoramaService.incrementRegenCount(selectedBeforeImage.id);
      const newRegenCount = incrementResponse.success && incrementResponse.regeneration_count !== undefined
        ? incrementResponse.regeneration_count
        : currentRegenCount + 1;

      // regenerationCountを更新
      thunkAPI.dispatch(panoramaActions.updateBeforeImageFromResponse({
        stubId: selectedBeforeImage.stubId,
        regenerationCount: newRegenCount,
      }));

      thunkAPI.dispatch(toastActions.toastSuccess("再生成が完了しました"));

    } catch (error) {
      console.error('regenPanorama error:', error);
      thunkAPI.dispatch(toastActions.toastError("再生成中にエラーが発生しました"));
    } finally {
      clearInterval(timer);
      thunkAPI.dispatch(panoramaActions.setIsLoading(false));
    }
  }
);

// 状態クリア
const clearPanoramaState = createAsyncThunk<void, void, { state: RootState }>(
  "panorama/clearPanoramaState",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedBeforeImage = getSelectedBeforeImage(state);

    if (selectedBeforeImage && selectedBeforeImage.id) {
      try {
        await PanoramaService.clearPanorama(selectedBeforeImage.id);
      } catch (error) {
        console.error('clearPanorama error:', error);
      }
    }

    thunkAPI.dispatch(panoramaActions.clearData());
  }
);

const panoramaSlice = createSlice({
  name: "panorama",
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStubId: (state, action: PayloadAction<string>) => {
      state.stubId = action.payload;
    },
    setBeforeImages: (state, action: PayloadAction<PanoramaBeforeImageState[]>) => {
      state.beforeImages = action.payload;
    },
    setSelectedBeforeImageStubId: (state, action: PayloadAction<string | null>) => {
      state.selectedBeforeImageStubId = action.payload;
    },
    removeBeforeImage: (state, action: PayloadAction<string>) => {
      const removeStubId = action.payload;
      state.beforeImages = state.beforeImages.filter(img => img.stubId !== removeStubId);
      // 削除した画像が選択中だった場合、選択を解除
      if (state.selectedBeforeImageStubId === removeStubId) {
        state.selectedBeforeImageStubId = state.beforeImages.length > 0 ? state.beforeImages[0].stubId : null;
        state.afterImages = [];
        state.selectedAfterImageId = null;
      }
    },
    updateBeforeImageFromResponse: (state, action: PayloadAction<Partial<{
      stubId: string;
      id: number;
      panoramaDesignId: number;
      regenerationCount: number;
      afterImages: PanoramaAfterImageState[];
      selectedAfterImageId: number | null;
      errorMessage: string | null;
    }>>) => {
      const targetStubId = action.payload.stubId || state.selectedBeforeImageStubId;
      const beforeImage = state.beforeImages.find(img => img.stubId === targetStubId);
      if (beforeImage) {
        if (action.payload.id !== undefined) beforeImage.id = action.payload.id;
        if (action.payload.panoramaDesignId !== undefined) beforeImage.panoramaDesignId = action.payload.panoramaDesignId;
        if (action.payload.regenerationCount !== undefined) beforeImage.regenerationCount = action.payload.regenerationCount;
        if (action.payload.afterImages !== undefined) beforeImage.afterImages = action.payload.afterImages;
        if (action.payload.selectedAfterImageId !== undefined) beforeImage.selectedAfterImageId = action.payload.selectedAfterImageId;
        if (action.payload.errorMessage !== undefined) beforeImage.errorMessage = action.payload.errorMessage;
      }
    },
    setAfterImages: (state, action: PayloadAction<PanoramaAfterImageState[]>) => {
      state.afterImages = action.payload;
    },
    addAfterImages: (state, action: PayloadAction<PanoramaAfterImageState[]>) => {
      state.afterImages = [...state.afterImages, ...action.payload];
    },
    setSelectedAfterImageId: (state, action: PayloadAction<number | null>) => {
      state.selectedAfterImageId = action.payload;
    },
    setRegenerationCount: (state, action: PayloadAction<number>) => {
      state.regenerationCount = action.payload;
    },
    setRunTime: (state, action: PayloadAction<number>) => {
      state.runTime = action.payload;
    },
    setMaxExecTime: (state, action: PayloadAction<number>) => {
      state.maxExecTime = action.payload;
    },
    setIsConfirmPopupShow: (state, action: PayloadAction<boolean>) => {
      state.isConfirmPopupShow = action.payload;
    },
    setIsRegenPopupShow: (state, action: PayloadAction<boolean>) => {
      state.isRegenPopupShow = action.payload;
    },
    setIsPhotoTooBigPopupShow: (state, action: PayloadAction<boolean>) => {
      state.isPhotoTooBigPopupShow = action.payload;
    },
    setIsAspectRatioErrorShow: (state, action: PayloadAction<boolean>) => {
      state.isAspectRatioErrorShow = action.payload;
    },
    setIsClearAllPopupShow: (state, action: PayloadAction<boolean>) => {
      state.isClearAllPopupShow = action.payload;
    },
    setErrorMessage: (state, action: PayloadAction<string | null>) => {
      state.errorMessage = action.payload;
    },
    // 生成進捗管理
    setGenerationProgress: (state, action: PayloadAction<{
      stubId: string;
      current: number;
      total: number;
    }>) => {
      state.generatingImages[action.payload.stubId] = {
        current: action.payload.current,
        total: action.payload.total,
      };
    },
    clearGenerationProgress: (state, action: PayloadAction<string>) => {
      delete state.generatingImages[action.payload];
    },
    clearData: (state) => {
      state.isLoading = false;
      state.stubId = getRandId().toString();
      state.beforeImages = [];
      state.selectedBeforeImageStubId = null;
      state.afterImages = [];
      state.selectedAfterImageId = null;
      state.regenerationCount = 0;
      state.runTime = 0;
      state.isConfirmPopupShow = false;
      state.isRegenPopupShow = false;
      state.isPhotoTooBigPopupShow = false;
      state.isAspectRatioErrorShow = false;
      state.isClearAllPopupShow = false;
      state.errorMessage = null;
      state.generatingImages = {};
    },
  },
});

// アクションとリデューサーをエクスポート
export const panoramaActions = {
  ...panoramaSlice.actions,
  uploadPanoramaImages,
  processPanorama,
  regenPanorama,
  clearPanoramaState,
};

export default panoramaSlice.reducer;
